import { useAtomValue } from 'jotai';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import { type ReactNode, useMemo } from 'react';
import { paymentMethodWithPersistence, selectedPlanAtom } from '@features/Stripe/atoms';
import { useGetStripeProductId } from '@features/Stripe/hooks/query/use-get-stripe-product-id';
import { useIntl } from '@features/intl';
import { useStores } from 'src/components/common/root-store-provider/root-store-provider';
import { ABC_ASYM_TEST_NAME, ACADEMIA_UPSELL_TEST_NAME, ASYM_WOMEN_SCREEN_TEST_NAME, AbcAsymTestCaseName, BEFORE_AFTER_PAYWALL_TEST_NAME, BUNDLE_VARIANT_DIFFERENT_PLANS, CHEEP_CHALLENGE_TEST_NAME, CheepChallengeTestCaseName, HIDE_PAYPAL_TEST_NAME, MAXI_WELCOME_SCREEN, MEWING_BUNDLE_TEST_NAME, MEWING_PT_BUNDLE_VARIANT_DIFFERENT_PLANS, MEWING_TT_BUNDLE_VARIANT_DIFFERENT_PLANS, MEWING_WOMEN_VARIANT_DIFFERENT_PLANS, OLD_CARD_TEST_NAME, SCANNER_TEST_NAME, SHOPPER_TEST_NAME, SIMPLE_PAYWALL_TEST_NAME, V21_PT_BUNDLE_DIFFERENT_PLANS, V21_TT_BUNDLE_DIFFERENT_PLANS } from 'src/constants/analytics';
import { routes } from 'src/constants/navigation';
import { ASYM_VARIANTS, EMAIL_VARIANTS, MAXI_VARIANTS, MEWING_VARIANTS, SHORT_VARIANTS, type TpStripeVariants } from 'src/constants/variants';
import { FlagsProvider } from './FlaggedUtil';
const FEATURE_FLAG_AB_TESTS = new Map([['isScannerEnabled', {
  feature: 'scannerFeature',
  testName: SCANNER_TEST_NAME
}], ['isSimplePaywallEnabled', {
  feature: 'simplePaywallFeature',
  testName: SIMPLE_PAYWALL_TEST_NAME
}], ['isAcademyUpsellEnabled', {
  feature: 'academyUpsellFeature',
  testName: ACADEMIA_UPSELL_TEST_NAME
}], ['isBeforeAfterPaywallTestEnabled', {
  feature: 'beforeAfterPaywallTest',
  testName: BEFORE_AFTER_PAYWALL_TEST_NAME
}], ['isHidePayPalTestEnabled', {
  feature: 'hidePayPalTest',
  testName: HIDE_PAYPAL_TEST_NAME
}], ['isShopperTestEnabled', {
  feature: 'shopperFeature',
  testName: SHOPPER_TEST_NAME
}], ['isOldCardTestEnabled', {
  feature: 'oldCardFeature',
  testName: OLD_CARD_TEST_NAME
}], ['isMewingBundleEnabled', {
  feature: 'mewingBundleFeature',
  testName: MEWING_BUNDLE_TEST_NAME
}], ['isDifferentBundlePriceEnabled', {
  feature: 'differentBundlePriceFeature',
  testName: BUNDLE_VARIANT_DIFFERENT_PLANS
}], ['isMewingPTDifferentBundlePriceEnabled', {
  feature: 'mewingPTDifferentBundlePriceFeature',
  testName: MEWING_PT_BUNDLE_VARIANT_DIFFERENT_PLANS
}], ['isMewingTTDifferentBundlePriceEnabled', {
  feature: 'mewingTTDifferentBundlePriceFeature',
  testName: MEWING_TT_BUNDLE_VARIANT_DIFFERENT_PLANS
}], ['isWomenDifferentBundlePriceEnabled', {
  feature: 'womenDifferentBundlePriceFeature',
  testName: MEWING_WOMEN_VARIANT_DIFFERENT_PLANS
}], ['isV21TTDifferentBundlePriceEnabled', {
  feature: 'v21TTDifferentBundlePriceFeature',
  testName: V21_TT_BUNDLE_DIFFERENT_PLANS
}], ['isV21PTDifferentBundlePriceEnabled', {
  feature: 'v21PTDifferentBundlePriceFeature',
  testName: V21_PT_BUNDLE_DIFFERENT_PLANS
}], ['isMaxiFirstScreenTest', {
  feature: 'maxiFirstScreenTest',
  testName: MAXI_WELCOME_SCREEN
}], ['isAsymWomenScreenTest', {
  feature: 'asymWomenScreenTest',
  testName: ASYM_WOMEN_SCREEN_TEST_NAME
}]]);
const ABC_ASYM_FEATURE_FLAGS = {
  abcAsymDefault: AbcAsymTestCaseName.DEFAULT,
  abcAsymQuestion: AbcAsymTestCaseName.QUESTION,
  abcAsymHead: AbcAsymTestCaseName.HEAD
} as const;
export const FEATURE_FLAGS = [...Array.from(FEATURE_FLAG_AB_TESTS.values()).map(flag => flag.feature), ...Object.keys(ABC_ASYM_FEATURE_FLAGS), 'usFeature', 'introOfferFeature', 'cheapOfferFeature', 'cheapOfferInBeginning', 'cheapOfferInMiddle', 'mewingFeature', 'emailFeature', 'asymmetryFeature', 'asymmetryWomenFeature', 'maxiFeature', 'bonusFeature', 'fbPixelSept2025Feature'] as const;
type TpFeatureFlags = Record<(typeof FEATURE_FLAGS)[number], boolean>;
const startsWithB = (inputString: string | undefined | null): boolean => /^B/.test(inputString || '');
export const FeatureFlagProvider = observer(({
  children
}: {
  children: ReactNode;
}) => {
  const {
    quizStore: {
      countryCode,
      stepToPropertyMap
    },
    analyticsStore: {
      getCaseNameByTestName,
      segment
    },
    authStore: {
      variant
    }
  } = useStores();
  const {
    pureText
  } = useIntl();
  const genderValue = useMemo(() => stepToPropertyMap?.mwGender?.answer, [stepToPropertyMap?.mwGender?.answer]);
  const selectedPlanInfo = useAtomValue(selectedPlanAtom);
  const {
    query,
    pathname
  } = useRouter();
  const featuresFromQuery = useMemo(() => {
    if (typeof window === 'undefined') return [];
    const featuresFromStorage = sessionStorage.getItem('features')?.split(',') || [];
    if (query.features && query['hard_reset'] == undefined) {
      const features = typeof query.features === 'string' ? query.features.split(',') : query.features;
      sessionStorage.setItem('features', features.toString());
      return features;
    }
    return featuresFromStorage;
  }, [query.features, query['hard_reset']]);

  // Read observable cases on every render: their segment name can stay the same.
  const featuresAbTestingEnabled = Object.fromEntries(segment ? Array.from(FEATURE_FLAG_AB_TESTS.values(), ({
    feature,
    testName
  }) => [feature, startsWithB(getCaseNameByTestName(testName))]) : []);
  const abcAsymCaseName = getCaseNameByTestName(ABC_ASYM_TEST_NAME);
  const forcedAbcAsymFeature = featuresFromQuery.find(feature => Object.keys(ABC_ASYM_FEATURE_FLAGS).includes(feature));
  // The first ABC override selects one case, even if analytics assigned another.
  const abcAsymFeatures = Object.fromEntries(Object.entries(ABC_ASYM_FEATURE_FLAGS).map(([feature, caseName]) => [feature, forcedAbcAsymFeature ? feature === forcedAbcAsymFeature : Boolean(segment) && abcAsymCaseName === caseName]));
  const isCheapOfferInBeginning = getCaseNameByTestName(CHEEP_CHALLENGE_TEST_NAME) === CheepChallengeTestCaseName.ONBOARDING_BEGIN;
  const isCheapOfferInMiddleEnabled = getCaseNameByTestName(CHEEP_CHALLENGE_TEST_NAME) === CheepChallengeTestCaseName.ONBOARDING_MIDDLE;
  const isAcademyV3Enabled = true;
  const isBundleFeatureEnabled = !!selectedPlanInfo.bundle;
  // BUNDLE_VARIANTS.includes(variant as TpStripeVariants) ||
  // !ALL_VARIANTS.some(
  //   (item) => item.variant_name === (variant as unknown as any),
  // ) ||
  // variant === 'default' ||
  // featuresAbTestingEnabled.mewingBundleFeature;

  const isCheapOfferEnabled = isCheapOfferInBeginning || isCheapOfferInMiddleEnabled;
  const {
    isUsProductIdValid
  } = useGetStripeProductId({
    variant: variant as TpStripeVariants ?? 'default' as TpStripeVariants,
    enabled: isCheapOfferEnabled || pathname === routes.newPlanPaywall
  });
  const isVariantUsEnabled = useMemo(() => countryCode === 'US' && isUsProductIdValid, [countryCode, isUsProductIdValid]);
  const isIntroOfferEnabled = useMemo(() => selectedPlanInfo.introOffer.length > 0 && Number(selectedPlanInfo.introOfferData.introOfferDuration) > 0, [selectedPlanInfo.introOffer, selectedPlanInfo.introOfferData.introOfferDuration]);
  const paymentMethod = useAtomValue(paymentMethodWithPersistence);
  const isPayedWithBraintreePayPal = paymentMethod === 'paypal';

  // onboarding feature tumblers
  const isFbPixelSept2025Enabled = variant === 'variant21pix' || variant === 'variant21of';
  const isStresslessOnboarding = variant === 'stressless';
  const isHealthFeatureEnabled = variant === 'health';
  const bonusFeatureEnabled = variant === 'bonus';
  const isEmailFeatureEnabled = EMAIL_VARIANTS.includes(variant as TpStripeVariants);
  const isMewingFeatureEnabled = MEWING_VARIANTS.includes(variant as TpStripeVariants);
  const isAsymmetryFeatureEnabled = ASYM_VARIANTS.includes(variant as TpStripeVariants);
  const isAsymmetryWomenFeatureEnabled = variant === 'asymmetry_women';
  const isMaxiFeatureEnabled = MAXI_VARIANTS.includes(variant as TpStripeVariants);
  const isShortFeatureEnabled = SHORT_VARIANTS.includes(variant as TpStripeVariants);
  const isShopperFeatureEnabled = featuresAbTestingEnabled.shopperFeature && countryCode === 'US';
  const isWomanMewingEnabled = isMewingFeatureEnabled && (variant.includes('woman') || variant.includes('women') && genderValue?.length === 0) || genderValue === pureText({
    id: 'Onboarding.mwGender.option_2'
  });
  const features = useMemo((): TpFeatureFlags => ({
    ...featuresAbTestingEnabled,
    shopperFeature: isShopperFeatureEnabled,
    stresslessFeature: isStresslessOnboarding,
    usFeature: isVariantUsEnabled,
    introOfferFeature: isIntroOfferEnabled,
    healthFeature: isHealthFeatureEnabled,
    cheapOfferFeature: isCheapOfferEnabled,
    isBundleFeature: isBundleFeatureEnabled,
    isWomanMewing: isWomanMewingEnabled,
    academyUpsellV3Feature:
    // вот здесь не понимаю зачем isPayedWithBraintreePayPal,
    // если удалить, то тогда остальные условия с v3 как дефолт станут намного проще
    // Потому что апсел недоступен для paypall
    !isPayedWithBraintreePayPal && isAcademyV3Enabled,
    cheapOfferInBeginning: isCheapOfferInBeginning,
    cheapOfferInMiddle: isCheapOfferInMiddleEnabled,
    mewingFeature: isMewingFeatureEnabled,
    emailFeature: isEmailFeatureEnabled,
    asymmetryFeature: isAsymmetryFeatureEnabled,
    asymmetryWomenFeature: isAsymmetryWomenFeatureEnabled,
    maxiFeature: isMaxiFeatureEnabled,
    shortFeature: isShortFeatureEnabled,
    bonusFeature: bonusFeatureEnabled,
    academyUpsellFeature: !isPayedWithBraintreePayPal && variant === 'variant21' ? featuresAbTestingEnabled.academyUpsellFeature : true,
    fbPixelSept2025Feature: isFbPixelSept2025Enabled,
    ...Object.fromEntries(featuresFromQuery.map(feature => [feature, true])),
    ...abcAsymFeatures
  }), [isFbPixelSept2025Enabled, isShopperFeatureEnabled, isMewingFeatureEnabled, isEmailFeatureEnabled, isCheapOfferInBeginning, isCheapOfferInMiddleEnabled, isHealthFeatureEnabled, isStresslessOnboarding, featuresAbTestingEnabled, abcAsymFeatures, isVariantUsEnabled, isIntroOfferEnabled, bonusFeatureEnabled, featuresFromQuery, isShortFeatureEnabled, isBundleFeatureEnabled, isAsymmetryFeatureEnabled, isAsymmetryWomenFeatureEnabled, isMaxiFeatureEnabled, isCheapOfferEnabled, isPayedWithBraintreePayPal, isWomanMewingEnabled, variant]);
  return <FlagsProvider features={features}>{children}</FlagsProvider>;
});